import { AgendaTabs } from '../shell/AgendaTabs';
import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { CalendarEntry } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { formatDayHeading, formatTime, fromLocalInput, groupByDay, toLocalInput } from './dates';

type View = 'mes' | 'semana' | 'dia';
const VIEWS: { key: View; label: string }[] = [
  { key: 'mes', label: 'Mes' },
  { key: 'semana', label: 'Semana' },
  { key: 'dia', label: 'Día' },
];
const WEEKDAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const pad = (n: number) => String(n).padStart(2, '0');
/** Fecha local como AAAA-MM-DD (para la dirección: ?fecha=2026-10-14). */
const toParam = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
function fromParam(value: string | null): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '');
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : startOfToday();
}
/** Suma días por calendario (no por milisegundos), para no fallar con cambios de horario. */
const plusDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const startOfWeek = (d: Date) => plusDays(d, -d.getDay());
const sameDay = (a: Date, b: Date) => toParam(a) === toParam(b);

/** Días que se muestran y rango a pedir según la vista. */
function rangeOf(view: View, anchor: Date): { days: Date[]; from: Date; to: Date } {
  if (view === 'dia') return { days: [anchor], from: anchor, to: plusDays(anchor, 1) };
  if (view === 'semana') {
    const from = startOfWeek(anchor);
    return { days: Array.from({ length: 7 }, (_, i) => plusDays(from, i)), from, to: plusDays(from, 7) };
  }
  // Siempre 6 semanas (como cualquier calendario): al final del mes se ven también los primeros días
  // del siguiente, para no perder lo que viene en unos días.
  const from = startOfWeek(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
  return { days: Array.from({ length: 42 }, (_, i) => plusDays(from, i)), from, to: plusDays(from, 42) };
}

function titleOf(view: View, anchor: Date, days: Date[]): string {
  const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
  if (view === 'mes') return cap(anchor.toLocaleDateString('es', { month: 'long', year: 'numeric' }));
  if (view === 'dia') return formatDayHeading(anchor);
  const a = days[0]!;
  const b = days[6]!;
  return a.getMonth() === b.getMonth()
    ? `${a.getDate()} – ${b.getDate()} de ${b.toLocaleDateString('es', { month: 'long', year: 'numeric' })}`
    : `${a.toLocaleDateString('es', { day: 'numeric', month: 'short' })} – ${b.toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

/** Agenda de la Iglesia activa como calendario: por mes, semana o día. Cada elemento enlaza a su fuente. */
export function CalendarPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const [params, setParams] = useSearchParams();
  // En el celular los cuadritos del mes no se leen: ahí abre en «Semana».
  const [initialView] = useState<View>(() => (typeof window !== 'undefined' && window.matchMedia?.('(max-width: 640px)')?.matches ? 'semana' : 'mes'));
  const view: View = VIEWS.some((v) => v.key === params.get('vista')) ? (params.get('vista') as View) : initialView;
  const anchor = fromParam(params.get('fecha'));
  const [editingEntry, setEditingEntry] = useState<CalendarEntry | null>(null);
  const { days, from, to } = rangeOf(view, anchor);
  const entries = useAsync(
    (signal) =>
      churchId
        ? api
            .get<{ items: CalendarEntry[] }>(`/calendar/churches/${churchId}/entries?from=${from.toISOString()}&to=${to.toISOString()}`, signal)
            .then((r) => r.items)
        : Promise.resolve([]),
    [churchId, from.getTime(), to.getTime()],
  );
  const canManage = me.can('calendar.manage');
  // «Nuevo evento» es el mismo formulario de Eventos, para quien puede crearlos ahí.
  const eventOptions = useAsync(
    (signal) =>
      churchId
        ? api.get<{ canCreateGeneral: boolean; ministries: unknown[] }>(`/calendar/churches/${churchId}/church-events/options`, signal).catch(() => null)
        : Promise.resolve(null),
    [churchId],
  );
  const canCreateEvent = canManage || (eventOptions.status === 'ready' && !!eventOptions.data && (eventOptions.data.canCreateGeneral || eventOptions.data.ministries.length > 0));
  // Los eventos sencillos de antes (sin página) se abren aquí para editarlos o cancelarlos.
  const onEdit = canManage ? setEditingEntry : undefined;

  const go = (next: { vista?: View; fecha?: Date }) => {
    const v = next.vista ?? view;
    const f = next.fecha ?? anchor;
    setParams({ ...(v !== initialView ? { vista: v } : {}), ...(sameDay(f, startOfToday()) ? {} : { fecha: toParam(f) }) }, { replace: true });
  };
  const step = (dir: 1 | -1) =>
    go({ fecha: view === 'mes' ? new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1) : plusDays(anchor, view === 'semana' ? 7 * dir : dir) });
  const items = entries.status === 'ready' ? entries.data : [];
  const onDay = (d: Date) => {
    const start = d.getTime();
    const end = plusDays(d, 1).getTime();
    return items.filter((e) => new Date(e.startsAt).getTime() < end && new Date(e.endsAt).getTime() > start - (e.startsAt === e.endsAt ? 1 : 0));
  };

  return (
    <div className="page">
      <AgendaTabs />
      <header className="page__header page__header--actions">
        <div>
          <h1>Calendario</h1>
          {me.activeContext ? <p className="muted">{me.activeContext.churchName}</p> : null}
        </div>
        {canCreateEvent ? (
          <Link className="btn btn--primary" to="/app/eventos/nuevo">
            Nuevo evento
          </Link>
        ) : null}
      </header>

      {!churchId ? <EmptyState title="Elige primero la iglesia en la que actúas." /> : null}
      {editingEntry && churchId ? (
        <EditEventCard
          key={editingEntry.id}
          churchId={churchId}
          entry={editingEntry}
          onDone={() => {
            setEditingEntry(null);
            entries.reload();
          }}
          onCancel={() => setEditingEntry(null)}
        />
      ) : null}

      <div className="cal-toolbar">
        <div className="cal-toolbar__nav">
          <Button variant="secondary" aria-label="Anterior" onClick={() => step(-1)}>
            ‹
          </Button>
          <Button variant="secondary" onClick={() => go({ fecha: startOfToday() })}>
            Hoy
          </Button>
          <Button variant="secondary" aria-label="Siguiente" onClick={() => step(1)}>
            ›
          </Button>
          <h2 className="cal-toolbar__title">{titleOf(view, anchor, days)}</h2>
        </div>
        <div className="cal-views" role="group" aria-label="Ver por">
          {VIEWS.map((v) => (
            <button key={v.key} type="button" className={`cal-views__option${v.key === view ? ' cal-views__option--active' : ''}`} aria-pressed={v.key === view} onClick={() => go({ vista: v.key })}>
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {entries.status === 'loading' ? <Loading /> : null}
      {entries.status === 'error' ? <ErrorState error={entries.error} onRetry={entries.reload} /> : null}

      {entries.status === 'ready' && view === 'mes' ? (
        <div className="cal-month" role="grid" aria-label={titleOf(view, anchor, days)}>
          {WEEKDAYS.map((w) => (
            <div key={w} className="cal-month__weekday" role="columnheader">
              {w}
            </div>
          ))}
          {days.map((d) => {
            const list = onDay(d);
            const outside = d.getMonth() !== anchor.getMonth();
            return (
              <div key={toParam(d)} role="gridcell" className={`cal-day${outside ? ' cal-day--outside' : ''}${sameDay(d, startOfToday()) ? ' cal-day--today' : ''}`}>
                <button type="button" className="cal-day__number" aria-label={`Ver ${formatDayHeading(d)}`} onClick={() => go({ vista: 'dia', fecha: d })}>
                  {d.getDate()}
                </button>
                <ul className="cal-chips">
                  {list.slice(0, 3).map((e) => (
                    <Chip key={e.id} entry={e} onEdit={onEdit} />
                  ))}
                </ul>
                {list.length > 3 ? (
                  <button type="button" className="cal-day__more" onClick={() => go({ vista: 'dia', fecha: d })}>
                    +{list.length - 3} más
                  </button>
                ) : null}
                {list.length ? <span className="cal-day__dots" aria-hidden="true">{list.slice(0, 4).map((e) => <i key={e.id} className={`cal-dot cal-dot--${e.sourceModule}`} />)}</span> : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {entries.status === 'ready' && view === 'semana' ? (
        <div className="cal-week">
          {days.map((d) => {
            const list = onDay(d);
            return (
              <section key={toParam(d)} className={`cal-week__day${sameDay(d, startOfToday()) ? ' cal-day--today' : ''}`} aria-label={formatDayHeading(d)}>
                <button type="button" className="cal-week__head" onClick={() => go({ vista: 'dia', fecha: d })}>
                  <span>{WEEKDAYS[d.getDay()]}</span> <strong>{d.getDate()}</strong>
                </button>
                {list.length ? (
                  <ul className="cal-chips">
                    {list.map((e) => (
                      <Chip key={e.id} entry={e} onEdit={onEdit} />
                    ))}
                  </ul>
                ) : (
                  <p className="small muted cal-week__empty">Libre</p>
                )}
              </section>
            );
          })}
        </div>
      ) : null}

      {entries.status === 'ready' && view === 'dia' ? (
        onDay(anchor).length ? (
          <Agenda entries={onDay(anchor)} onEdit={onEdit} />
        ) : (
          <Card>
            <EmptyState title="No hay nada agendado este día." />
          </Card>
        )
      ) : null}

      {entries.status === 'ready' && view !== 'dia' ? (
        <p className="cal-legend small" aria-label="Colores">
          {LEGEND.map(([key, label]) => (
            <span key={key}>
              <i className={`cal-dot cal-dot--${key}`} aria-hidden="true" /> {label}
            </span>
          ))}
        </p>
      ) : null}
    </div>
  );
}

const LEGEND: [string, string][] = [
  ['worship', 'Culto'],
  ['calendar', 'Evento'],
  ['meeting', 'Reunión'],
  ['class', 'Clase'],
  ['prayer', 'Oración'],
  ['rehearsal', 'Ensayo'],
];

/** Un elemento dentro del día: hora y título; el título abre su página. */
function Chip({ entry: e, onEdit }: { entry: CalendarEntry; onEdit?: ((e: CalendarEntry) => void) | undefined }) {
  const to = linkOf(e);
  const editable = !to && e.sourceModule === 'calendar' && onEdit;
  return (
    <li className={`cal-chip cal-chip--${e.sourceModule}${e.status === 'canceled' ? ' cal-chip--canceled' : ''}`} title={`${formatTime(e.startsAt)} ${e.title}${e.location ? ` · ${e.location}` : ''}`}>
      <span className="cal-chip__time">{formatTime(e.startsAt)}</span>{' '}
      {to ? (
        <Link to={to}>{e.title}</Link>
      ) : editable ? (
        <button type="button" className="link-button" onClick={() => editable(e)}>
          {e.title}
        </button>
      ) : (
        <span>{e.title}</span>
      )}
    </li>
  );
}

const linkOf = (e: CalendarEntry) =>
  e.link ?? (e.sourceModule === 'worship' ? (e.sourceId ? `/app/cultos/${e.sourceId}` : '/app/cultos') : e.sourceModule === 'event' && e.sourceId ? `/app/eventos/${e.sourceId}` : null);

const KINDS: Record<string, string> = { calendar: 'Evento', event: 'Evento', worship: 'Culto', meeting: 'Reunión', class: 'Clase', prayer: 'Oración', rehearsal: 'Ensayo' };

export function Agenda({ entries, onEdit }: { entries: CalendarEntry[]; onEdit?: ((e: CalendarEntry) => void) | undefined }) {
  return (
    <div className="agenda">
      {groupByDay(entries).map((day) => (
        <section key={day.key} className="agenda__day" aria-label={formatDayHeading(day.date)}>
          <h2 className="agenda__date">{formatDayHeading(day.date)}</h2>
          <ul className="list">
            {day.items.map((e) => {
              const kind = KINDS[e.sourceModule] ?? KINDS.calendar!;
              const to = linkOf(e);
              return (
                <li key={e.id} className={`list__item agenda__item agenda__item--${e.sourceModule}`}>
                  <span className="agenda__time">
                    {formatTime(e.startsAt)}
                    <span className="muted small">{formatTime(e.endsAt)}</span>
                  </span>
                  <span className="agenda__body">
                    {to ? (
                      <Link to={to}>
                        <strong>{e.title}</strong>
                      </Link>
                    ) : onEdit && e.sourceModule === 'calendar' ? (
                      <button type="button" className="link-button" onClick={() => onEdit(e)}>
                        <strong>{e.title}</strong>
                      </button>
                    ) : (
                      <strong>{e.title}</strong>
                    )}
                    {e.location ? <span className="muted small">{e.location}</span> : null}
                    {e.description ? <span className="muted small">{e.description}</span> : null}
                  </span>
                  <span className={`badge agenda__kind agenda__kind--${e.sourceModule}`}>{kind}</span>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

/** Un evento sencillo del calendario (de antes del formulario de Eventos): se corrige o se cancela aquí. */
function EditEventCard({ churchId, entry, onDone, onCancel }: { churchId: string; entry: CalendarEntry; onDone: () => void; onCancel: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const url = `/calendar/churches/${churchId}/events/${entry.id}`;

  async function send(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      onDone();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    void send(() =>
      api.put(url, {
        title: String(form.get('title')),
        location: String(form.get('location') || '') || null,
        description: String(form.get('description') || '') || null,
        startsAt: fromLocalInput(String(form.get('startsAt'))),
        endsAt: fromLocalInput(String(form.get('endsAt'))),
      }),
    );
  }

  function onCancelEvent(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const reason = String(new FormData(event.currentTarget).get('reason') ?? '').trim();
    void send(() => api.post(`${url}/cancel`, { reason }));
  }

  return (
    <Card title="Editar evento">
      {error ? <Alert>{error}</Alert> : null}
      {entry.status === 'canceled' ? <p className="muted small">Este evento está cancelado.</p> : null}
      <form className="form" onSubmit={onSubmit} noValidate>
        <Field label="Título" name="title" required minLength={2} maxLength={150} defaultValue={entry.title} />
        <div className="form__row">
          <Field label="Empieza" name="startsAt" type="datetime-local" required defaultValue={toLocalInput(new Date(entry.startsAt))} />
          <Field label="Termina" name="endsAt" type="datetime-local" required defaultValue={toLocalInput(new Date(entry.endsAt))} />
        </div>
        <Field label="Lugar" name="location" maxLength={200} defaultValue={entry.location ?? ''} />
        <Field label="Descripción" name="description" maxLength={2000} defaultValue={entry.description ?? ''} />
        <div className="actions">
          <Button type="submit" busy={busy}>
            Guardar
          </Button>
          {entry.status !== 'canceled' && !canceling ? (
            <Button type="button" variant="danger" onClick={() => setCanceling(true)}>
              Cancelar evento
            </Button>
          ) : null}
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cerrar
          </Button>
        </div>
      </form>
      {canceling ? (
        <form className="form" onSubmit={onCancelEvent} noValidate>
          <Field label="¿Por qué se cancela?" name="reason" required minLength={3} maxLength={500} />
          <div className="actions">
            <Button type="submit" variant="danger" busy={busy}>
              Cancelar el evento
            </Button>
            <Button type="button" variant="ghost" onClick={() => setCanceling(false)}>
              No cancelar
            </Button>
          </div>
        </form>
      ) : null}
    </Card>
  );
}

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}
